"use client";

import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  forwardRef,
  type ReactNode,
} from "react";

import { cn } from "@/lib/utils";

type KanbanColumnStyle = CSSProperties & {
  "--kanban-column-accent": string;
};

/**
 * Presentational kanban shells. BoardColumn keeps the live machinery —
 * virtualization, drag-and-drop, mutations, load-more — and composes these;
 * surfaces without live data (print, export, the marketing product bridge)
 * compose them from fixtures, and their module graph never reaches the
 * record-field renderers or the workspace client.
 */
export const BoardColumnView = forwardRef<
  HTMLElement,
  {
    title: string;
    /** The column accent, written as `--kanban-column-accent`. */
    color: string;
    count: number;
    /** Preformatted column subtotal; omitted when there is nothing to sum. */
    subtotal?: string | null;
    mobile?: boolean;
    children?: ReactNode;
  } & Omit<ComponentPropsWithoutRef<"section">, "color" | "title">
>(function BoardColumnView(
  { title, color, count, subtotal, mobile = false, className, style, children, ...sectionProps },
  ref,
) {
  const accentStyle: KanbanColumnStyle = {
    "--kanban-column-accent": color,
    ...style,
  };
  return (
    <section
      ref={ref}
      className={cn(
        "mlx-kanban-column",
        mobile ? "mlx-kanban-column-mobile" : "",
        className,
      )}
      style={accentStyle}
      {...sectionProps}
    >
      <div className="mlx-kanban-header">
        <div className="mlx-kanban-header-title">
          <span className="mlx-kanban-header-dot" />
          <h2 className="mlx-kanban-header-label">{title}</h2>
          <span className="text-xs text-muted-foreground">({count})</span>
        </div>
        {subtotal ? (
          <span className="text-xs tabular-nums text-muted-foreground">
            {subtotal}
          </span>
        ) : null}
      </div>
      {children}
    </section>
  );
});

export function BoardCardView({
  title,
  sumText,
  chips,
  footer,
  actions,
  onOpen,
  className,
  ...divProps
}: {
  title: string;
  /** Preformatted sum-field line under the title. */
  sumText?: string | null;
  /** The chip row's content; the row renders only when provided. */
  chips?: ReactNode;
  /** Below the body — the mobile move control in the application. */
  footer?: ReactNode;
  /** Absolutely-positioned controls — the delete button in the application. */
  actions?: ReactNode;
  /** When set, the card body is a button opening the record. */
  onOpen?: () => void;
} & Omit<ComponentPropsWithoutRef<"div">, "title">) {
  const body = (
    <>
      <div className="truncate text-sm font-semibold leading-tight">{title}</div>
      {sumText ? (
        <div className="mt-1.5 text-sm tabular-nums text-muted-foreground">
          {sumText}
        </div>
      ) : null}
      {chips ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/40 pt-2 text-xs text-muted-foreground">
          {chips}
        </div>
      ) : null}
    </>
  );

  return (
    <div className={cn("mlx-kanban-card group/card", className)} {...divProps}>
      {onOpen ? (
        <button
          type="button"
          className={cn("block w-full text-left", actions ? "pr-8" : "")}
          onClick={onOpen}
        >
          {body}
        </button>
      ) : (
        <div className={cn("block w-full text-left", actions ? "pr-8" : "")}>
          {body}
        </div>
      )}
      {footer}
      {actions}
    </div>
  );
}
